import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Plugs, Play, Planet, Key, Plus, UploadSimple, Copy, Broadcast, ArrowsClockwise, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const SITES = [
  { name: "NaturePulse", domain: "Nature", color: "#16A34A" },
  { name: "TravelerPulse.pro", domain: "Travel", color: "#F27A54" },
  { name: "FoodPulse", domain: "Food", color: "#F97316" },
  { name: "AlumniPulse.net", domain: "Institutions", color: "#7C3AED" },
  { name: "SocialPulse", domain: "Events", color: "#EC4899" },
];

const FILTERS = [
  ["country", "India", "Case-insensitive exact match"],
  ["state", "Kerala", "Case-insensitive exact match"],
  ["city", "Kochi", "Case-insensitive exact match"],
  ["category", "Nature", "Travel · Food · Institutions · Events · Nature · Geographic …"],
  ["tags", "beach,sunset", "Comma-separated; all must match"],
  ["license", "public_attribution", "Filter by rights/reuse permission"],
  ["site", "naturepulse", "Only photos syndicated to this sister site"],
  ["q", "hibiscus", "Free-text over title, caption, description, keywords, city"],
  ["updated_since", "2026-06-01T00:00:00Z", "ISO-8601 — delta sync (see next_since)"],
  ["limit / offset", "50 / 0", "Page size (max 100) and offset"],
];

function CodeBlock({ children, testid }) {
  const copy = () => { navigator.clipboard.writeText(children); toast.success("Copied"); };
  return (
    <div className="relative mt-3">
      <button onClick={copy} className="absolute right-2 top-2 rounded-md bg-white/10 px-2 py-1 text-[11px] text-zinc-200 hover:bg-white/20" data-testid={testid ? `${testid}-copy` : undefined}>
        <Copy size={12} className="inline" /> Copy
      </button>
      <pre className="overflow-x-auto rounded-md bg-zinc-900 p-4 pr-16 font-mono-p text-xs leading-relaxed text-zinc-100">{children}</pre>
    </div>
  );
}

export default function ApiDocs() {
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [domain, setDomain] = useState("");
  const [keys, setKeys] = useState([]);
  const [newName, setNewName] = useState("");
  const [newSite, setNewSite] = useState("");

  const loadKeys = () => api.get("/apikeys").then((r) => setKeys(r.data)).catch(() => {});
  useEffect(() => { loadKeys(); }, []);

  const createKey = async () => {
    if (!newName) return;
    await api.post("/apikeys", { name: newName, site: newSite });
    setNewName(""); setNewSite("");
    loadKeys();
    toast.success("API key created");
  };
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };
  const platformKey = keys.find((k) => k.id === "default")?.key || "pk_live_…";

  const run = async (d) => {
    setDomain(d); setLoading(true); setResp(null);
    try {
      const { data } = await api.get("/v1/photos", { params: d ? { category: d, limit: 5 } : { limit: 5 } });
      setResp(data);
    } catch (e) { setResp({ error: String(e) }); }
    setLoading(false);
  };

  return (
    <div className="mx-auto max-w-[1400px]">
      <p className="overline text-[#F27A54]">Reusable Core Service</p>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Cross-Site Content API</h1>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
        One engine, every Pulse site. Every photo has a permanent canonical ID (<b>PP-YYYY-#######</b>) and is exposed through a
        versioned read API, a write (ingest) pipeline, and outbound webhooks. Private data and exact GPS are never exposed —
        only approximate coordinates and publicly-reusable media.
      </p>

      {/* Quick nav */}
      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        {[["#read", "v1 Read API"], ["#ingest", "Ingest (write)"], ["#webhooks", "Webhooks"], ["#keys", "API Keys"]].map(([h, l]) => (
          <a key={h} href={h} className="rounded-full border border-border px-3 py-1 font-medium text-zinc-600 hover:border-[#F27A54] hover:text-[#F27A54]">{l}</a>
        ))}
      </div>

      {/* ============ v1 READ API ============ */}
      <div id="read" className="mt-8 flex items-center gap-2">
        <Planet size={20} weight="bold" className="text-[#F27A54]" />
        <h2 className="font-display text-xl font-black tracking-tight">v1 Read API</h2>
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">stable</span>
      </div>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
        Base URL <code className="rounded bg-zinc-100 px-1 py-0.5">{API}</code>. Auth is <b>optional</b> for the internal ecosystem —
        pass <code className="rounded bg-zinc-100 px-1 py-0.5">X-API-Key</code> to raise your daily quota and get per-key usage tracking.
      </p>

      <Card className="mt-4 rounded-md border border-border p-5 shadow-none">
        <h3 className="font-display font-bold">Endpoints</h3>
        <CodeBlock testid="v1-endpoints">{`GET  ${API}/v1/whoami          # verify your key + see daily quota
GET  ${API}/v1/photos          # list approved, publicly-reusable photos
GET  ${API}/v1/photos/{id}     # single photo by internal id (canonical_url + pp_id)`}</CodeBlock>

        <h4 className="mt-5 text-sm font-bold">Query parameters</h4>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-muted-foreground"><tr className="border-b border-border">
              <th className="py-2 pr-4 font-medium">Param</th><th className="py-2 pr-4 font-medium">Example</th><th className="py-2 font-medium">Notes</th>
            </tr></thead>
            <tbody>
              {FILTERS.map(([p, ex, note]) => (
                <tr key={p} className="border-b border-border/60">
                  <td className="py-2 pr-4 font-mono-p text-[#F27A54]">{p}</td>
                  <td className="py-2 pr-4 font-mono-p text-zinc-600">{ex}</td>
                  <td className="py-2 text-muted-foreground">{note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <CodeBlock testid="v1-curl">{`# List Nature photos in Kerala, 20 at a time
curl "${API}/v1/photos?category=Nature&state=Kerala&limit=20" \\
  -H "X-API-Key: ${platformKey}"

# -> { "version":"v1", "count":137, "returned":20, "offset":0,
#      "next_since":"2026-06-14T09:12:00Z", "photos":[ { "photo_id":"PP-2026-0001234", ... } ] }`}</CodeBlock>
      </Card>

      {/* Delta sync */}
      <Card className="mt-4 rounded-md border border-border p-5 shadow-none">
        <div className="flex items-center gap-2"><ArrowsClockwise size={18} weight="bold" className="text-[#F27A54]" /><h3 className="font-display font-bold">Delta sync (keep a mirror up to date)</h3></div>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Store the <code className="rounded bg-zinc-100 px-1 py-0.5">next_since</code> cursor returned by each call, then pass it back as
          <code className="mx-1 rounded bg-zinc-100 px-1 py-0.5">updated_since</code> on your next poll to fetch only photos created or changed since then.
        </p>
        <CodeBlock testid="v1-delta">{`# First full pull
curl "${API}/v1/photos?limit=100" -H "X-API-Key: ${platformKey}"
# save response.next_since  ->  "2026-06-14T09:12:00Z"

# Every 5 min after: only what changed
curl "${API}/v1/photos?updated_since=2026-06-14T09:12:00Z&limit=100" \\
  -H "X-API-Key: ${platformKey}"`}</CodeBlock>
      </Card>

      {/* Try it live */}
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card className="rounded-md border border-border p-5 shadow-none">
          <div className="flex items-center gap-2"><Play size={18} weight="bold" className="text-[#F27A54]" /><h3 className="font-display font-bold">Try it live — /v1/photos</h3></div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-md" onClick={() => run("")} data-testid="api-run-all">All</Button>
            {SITES.map((s) => (
              <Button key={s.name} variant="outline" className="gap-2 rounded-md" onClick={() => run(s.domain)}
                style={{ borderColor: s.color }} data-testid={`api-run-${s.domain}`}>
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />{s.name}
              </Button>
            ))}
          </div>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Play size={12} weight="fill" />Calls the live <code className="mx-1">v1/photos</code> endpoint (limit 5) with your published photos.</p>
        </Card>

        <Card className="rounded-md border border-border p-5 shadow-none">
          <h3 className="font-display font-bold">Response {domain && <span className="text-sm text-muted-foreground">· category={domain || "all"}</span>}</h3>
          <pre className="mt-3 h-64 overflow-auto rounded-md bg-zinc-50 p-3 font-mono-p text-[11px] leading-relaxed text-zinc-700" data-testid="api-response">
{loading ? "Loading…" : resp ? JSON.stringify(resp, null, 2) : "Run a query to see live JSON. Publish photos (Review → Approve & Publish) to populate results."}
          </pre>
        </Card>
      </div>

      {/* ============ INGEST ============ */}
      <div id="ingest" className="mt-10 flex items-center gap-2">
        <UploadSimple size={20} weight="bold" className="text-[#F27A54]" />
        <h2 className="font-display text-xl font-black tracking-tight">Ingest API (write)</h2>
      </div>
      <Card className="mt-4 rounded-md border border-border p-5 shadow-none">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Sister sites POST user-submitted photos here (file upload <b>or</b> an image URL). Each photo runs the full pipeline —
          EXIF, GPS, reverse-geocoding, AI vision, categorization, species ID, duplicate detection — and lands in the shared
          database tagged with its <b>source_site</b>. The submitter then <b>accepts the AI result as-is</b> or <b>edits</b> it before
          finalizing. Authenticate with <code className="mx-1 rounded bg-zinc-100 px-1 py-0.5">X-API-Key</code> (required here).
        </p>
        <CodeBlock testid="ingest-curl">{`# 1. Submit a photo (file or image_url)
curl -X POST ${API}/ingest/photo \\
  -H "X-API-Key: ${platformKey}" \\
  -F "file=@beach.jpg" \\
  -F "source_site=naturepulse.net" \\
  -F "external_id=post_9182" \\
  -F "title=Sunset at Fort Kochi" \\
  -F "author=Jane Doe" \\
  -F "privacy=public" \\
  -F "tags=beach,sunset"
# -> { id, status:"analyzing", finalize_url }

# 2. Poll until analyzed (AI title/caption/location/tags + editable_fields)
curl ${API}/ingest/status/{id} -H "X-API-Key: ${platformKey}"

# 3a. ACCEPT the AI result as-is
curl -X POST ${API}/ingest/photo/{id}/finalize \\
  -H "X-API-Key: ${platformKey}" -H "Content-Type: application/json" \\
  -d '{"accept": true, "approve": true}'

# 3b. Or EDIT then accept
curl -X POST ${API}/ingest/photo/{id}/finalize \\
  -H "X-API-Key: ${platformKey}" -H "Content-Type: application/json" \\
  -d '{"accept": false, "approve": true, "edits": {
        "title": "Chinese Fishing Nets, Kochi",
        "tags": ["kochi","heritage"],
        "location": {"city": "Kochi", "country": "India"}
      }}'`}</CodeBlock>
        <p className="mt-2 text-xs text-muted-foreground">Finalizing with a public reuse permission fires the <code className="rounded bg-zinc-100 px-1 py-0.5">photo.published</code> webhook automatically.</p>
      </Card>

      {/* ============ WEBHOOKS ============ */}
      <div id="webhooks" className="mt-10 flex items-center gap-2">
        <Broadcast size={20} weight="bold" className="text-[#F27A54]" />
        <h2 className="font-display text-xl font-black tracking-tight">Webhooks</h2>
      </div>
      <Card className="mt-4 rounded-md border border-border p-5 shadow-none">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Get pushed a signed event the instant a photo is approved and publicly reusable — no polling required. Register and
          monitor endpoints in the <Link to="/webhooks" className="font-semibold text-[#F27A54] underline">Webhook Console</Link>, or
          programmatically with your secret <code className="mx-1 rounded bg-zinc-100 px-1 py-0.5">X-API-Key</code>.
        </p>
        <CodeBlock testid="webhook-register">{`# Register (returns a signing secret — store it, shown once)
curl -X POST ${API}/v1/webhooks \\
  -H "X-API-Key: ${platformKey}" -H "Content-Type: application/json" \\
  -d '{"url": "https://naturepulse.net/hooks/photopulse", "events": ["photo.published"]}'
# -> { id, url, secret:"whsec_…", events:["photo.published"] }

# List / delete
curl ${API}/v1/webhooks -H "X-API-Key: ${platformKey}"
curl -X DELETE ${API}/v1/webhooks/{id} -H "X-API-Key: ${platformKey}"`}</CodeBlock>

        <h4 className="mt-5 text-sm font-bold">Event payload — <span className="font-mono-p text-[#F27A54]">photo.published</span></h4>
        <CodeBlock testid="webhook-payload">{`POST  https://naturepulse.net/hooks/photopulse
Headers:
  X-PhotoPulse-Event: photo.published
  X-PhotoPulse-Signature: sha256=<hex hmac of the raw body>

{
  "event": "photo.published",
  "at": "2026-06-14T09:12:00Z",
  "photo": {
    "id": "a1b2c3d4-…",
    "pp_id": "PP-2026-0001234",
    "canonical_url": "https://photopulse.pro/photo/PP-2026-0001234",
    "title": "Sunset at Fort Kochi",
    "image_url": "https://…/api/photos/{id}/image?variant=display",
    "location": { "city": "Kochi", "country": "India" },
    "reuse_permission": "public_attribution",
    "attribution": "Photo by Jane Doe / PhotoPulse"
  }
}`}</CodeBlock>

        <div className="mt-5 flex items-center gap-2"><ShieldCheck size={16} weight="bold" className="text-[#F27A54]" /><h4 className="text-sm font-bold">Verify the signature</h4></div>
        <CodeBlock testid="webhook-verify">{`import hmac, hashlib

def verify(raw_body: bytes, header: str, secret: str) -> bool:
    expected = "sha256=" + hmac.new(secret.encode(), raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, header)

# Node:
# const expected = "sha256=" + crypto.createHmac("sha256", secret).update(rawBody).digest("hex");`}</CodeBlock>
        <p className="mt-3 text-xs text-muted-foreground">Return any 2xx to acknowledge. Non-2xx or timeouts are logged as failures in the Webhook Console. Deduplicated: each photo fires exactly once.</p>
      </Card>

      {/* ============ KEYS ============ */}
      <div id="keys" className="mt-10 flex items-center gap-2">
        <Key size={20} weight="bold" className="text-[#F27A54]" />
        <h2 className="font-display text-xl font-black tracking-tight">API Keys</h2>
      </div>
      <Card className="mt-4 rounded-md border border-border p-5 shadow-none">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Each key returns a secret <code className="mx-1 rounded bg-zinc-100 px-1 py-0.5">pk_live_…</code> (server-side, never expose in a browser)
          and a publishable <code className="mx-1 rounded bg-zinc-100 px-1 py-0.5">ppk_…</code> for browser embed widgets.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div><label className="overline text-[10px]">Key name</label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. NaturePulse prod" className="mt-1 h-9 w-52 rounded-md" data-testid="apikey-name" /></div>
          <div><label className="overline text-[10px]">Site (optional)</label>
            <Input value={newSite} onChange={(e) => setNewSite(e.target.value)} placeholder="naturepulse.net" className="mt-1 h-9 w-52 rounded-md" /></div>
          <Button onClick={createKey} className="h-9 gap-1 rounded-md bg-[#F27A54] hover:bg-[#d9633f]" data-testid="apikey-create"><Plus size={15} weight="bold" />Generate</Button>
        </div>
        <div className="mt-4 space-y-2">
          {keys.map((k) => (
            <div key={k.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold">{k.name} {k.site && <span className="text-xs font-normal text-muted-foreground">· {k.site}</span>}</p>
                <p className="truncate font-mono-p text-xs text-zinc-500">{k.key}{k.pub_key && <span className="ml-2 text-zinc-400">pub: {k.pub_key}</span>}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${k.active ? "bg-emerald-500" : "bg-zinc-300"}`} />
                <Button size="sm" variant="outline" className="h-8 gap-1 rounded-md" onClick={() => copy(k.key)}><Copy size={13} />Copy</Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {SITES.map((s) => (
          <div key={s.name} className="rounded-md border border-border p-4">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color, display: "inline-block" }} />
            <p className="mt-2 font-display text-sm font-bold">{s.name}</p>
            <p className="font-mono-p text-xs text-muted-foreground">category={s.domain}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
